import { AddProjectTeamRoleModal } from "@/containers/Project/components/ProjectTeam";
import { ProjectTeamMemberField } from "@/containers/Project/components/ProjectTeam/components/ProjectTeamMemberField";
import { IProjectTeamRole, ITranslation } from "@/interfaces";
import { useProjectContext } from "@/providers";
import { useGetFormLookupsQuery } from "@/services";
import { Button, FormInstance, Row, Space, Typography } from "antd";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

const { Title } = Typography;

interface IProps {
  regionTeamRoles: IProjectTeamRole[];
  form: FormInstance;
  editMode: boolean;
  regionId: string;
}

export const RegionDisplayTab = ({
  regionTeamRoles,
  form,
  editMode,
  regionId,
}: IProps) => {
  const intl = useIntl();
  const [displayRoles, setDisplayRoles] = useState<IProjectTeamRole[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const { templateRolesMap } = useProjectContext();

  const [functionalAreaDictionary, setFunctionalAreaDictionary] = useState<{
    [key: string]: IProjectTeamRole[];
  }>({});

  const [functionalAreas, setFunctionalAreas] = useState<{
    [functionalArea: string]: ITranslation;
  }>({});

  const { data: { intake } = {} } = useGetFormLookupsQuery();

  useEffect(() => {
    if (!intake) return;

    setFunctionalAreas(intake.functionalArea);
  }, [intake]);

  useEffect(() => {
    setDisplayRoles(regionTeamRoles || []);
  }, [regionTeamRoles]);

  useEffect(() => {
    setFunctionalAreaDictionary(_.groupBy(displayRoles, "functionalAreaId"));
  }, [displayRoles]);

  const handleDeleteRole = (role: IProjectTeamRole): void => {
    form.setFieldsValue({
      [role.id]: { ...role, isDeleted: true, isNewRole: false },
    });
    setDisplayRoles((prevState) => prevState.filter((x) => x.id !== role.id));
  };

  const handleAddRole = (role: IProjectTeamRole): void => {
    form.setFieldsValue({
      [role.id]: {
        ...role,
        isNewRole: true,
        isDeleted: false,
      },
    });
    setDisplayRoles((prevState) => [...prevState, role]);
  };

  if (_.isNil(regionTeamRoles)) return <></>;

  return (
    <Space direction="vertical" size="small" className="w-full">
      {editMode && (
        <Button
          data-test-id={`${regionId}-add-project-team-role`}
          onClick={(): void => setModalOpen(true)}
        >
          {intl.formatMessage({ id: "projects.teamAddRole" })}
        </Button>
      )}
      <>
        {Object.keys(functionalAreaDictionary).map((functionalArea) => {
          const teamRoles = functionalAreaDictionary[functionalArea];

          return teamRoles ? (
            <>
              <Title level={5}>
                {functionalAreas[functionalArea]?.fallback}
              </Title>
              <Row>
                {teamRoles.map((teamRole) => (
                  <ProjectTeamMemberField
                    key={teamRole.id}
                    teamRole={teamRole}
                    editMode={editMode}
                    handleDelete={(role: IProjectTeamRole): void =>
                      handleDeleteRole(role)
                    }
                    templateRolesMap={templateRolesMap}
                  />
                ))}
              </Row>
            </>
          ) : (
            <></>
          );
        })}
      </>
      <AddProjectTeamRoleModal
        isOpen={modalOpen}
        currentProjectTeamRolesByFunctionalArea={functionalAreaDictionary}
        functionalAreas={functionalAreas}
        onClose={(): void => setModalOpen(false)}
        onOk={(role: IProjectTeamRole): void => handleAddRole(role)}
        regionId={regionId}
      />
    </Space>
  );
};
